"use client";

import React, { useState, useEffect } from "react";
import { X, Check, ArrowRight } from "lucide-react";

interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialService?: string;
}

export function OrderModal({ isOpen, onClose, initialService }: OrderModalProps) {
  const [service, setService] = useState(initialService || "Визитки и полиграфия");
  const [quantity, setQuantity] = useState("500");
  const [paper, setPaper] = useState("chalk-300");
  const [urgency, setUrgency] = useState("standard");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isCalculated, setIsCalculated] = useState(false);

  useEffect(() => {
    if (initialService) {
      setService(initialService);
    }
  }, [initialService]);

  if (!isOpen) return null;

  const calculatePrice = () => {
    const qty = parseInt(quantity, 10) || 100;
    let basePerUnit = 4.5;
    if (service.includes("Каталог") || service.includes("брошюр")) basePerUnit = 45;
    if (service.includes("Баннер") || service.includes("вывеск")) basePerUnit = 120;
    if (service.includes("Упаковк")) basePerUnit = 35;
    if (service.includes("Наклейк")) basePerUnit = 8;

    let paperMultiplier = 1.0;
    if (paper === "touch-cover") paperMultiplier = 1.7;
    if (paper === "designer") paperMultiplier = 1.4;

    let urgencyMultiplier = urgency === "express" ? 1.5 : 1.0;

    const total = Math.round(qty * basePerUnit * paperMultiplier * urgencyMultiplier);
    return total.toLocaleString("ru-RU");
  };

  const handleFinish = (e: React.FormEvent) => {
    e.preventDefault();
    setIsCalculated(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-white dark:bg-[#0E121E] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-6 sm:p-7 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {isCalculated ? (
          <div className="py-6 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-emerald-50 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-400 flex items-center justify-center mx-auto">
              <Check className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
              Расчёт подготовлен
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 max-w-sm mx-auto leading-relaxed">
              Ориентировочная стоимость:{" "}
              <span className="font-semibold text-slate-900 dark:text-white">
                {calculatePrice()} ₽
              </span>
              . Технолог свяжется с вами в течение 15 минут для подтверждения параметров тиража.
            </p>
            <div className="pt-2">
              <button
                onClick={onClose}
                className="px-6 py-2.5 rounded-lg text-xs font-medium bg-[#0a3d90] dark:bg-[#3b82f6] text-white hover:bg-[#083070] dark:hover:bg-[#2563eb] transition-colors"
              >
                Закрыть окно
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-4">
              <span className="text-xs font-mono text-slate-500 dark:text-slate-400 block mb-1">
                Калькулятор тиража
              </span>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white tracking-tight">
                Быстрый расчёт заказа
              </h3>
            </div>

            <form onSubmit={handleFinish} className="space-y-3.5 text-xs sm:text-sm">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Услуга или продукция
                </label>
                <select
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:border-[#0a3d90]"
                >
                  <option value="Визитки и полиграфия">Визитки и полиграфия</option>
                  <option value="Листовки и буклеты">Листовки и буклеты</option>
                  <option value="Каталоги и брошюры">Каталоги и брошюры</option>
                  <option value="Наклейки и этикетки">Наклейки и этикетки</option>
                  <option value="Баннеры и вывески">Баннеры и вывески</option>
                  <option value="Упаковка и коробки">Упаковка и коробки</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Тираж
                  </label>
                  <select
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:border-[#0a3d90] font-mono"
                  >
                    <option value="100">100 шт</option>
                    <option value="300">300 шт</option>
                    <option value="500">500 шт</option>
                    <option value="1000">1 000 шт</option>
                    <option value="3000">3 000 шт</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Материал
                  </label>
                  <select
                    value={paper}
                    onChange={(e) => setPaper(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:border-[#0a3d90]"
                  >
                    <option value="chalk-300">Мелованная 300 г/м²</option>
                    <option value="touch-cover">Тач-кавер (Soft Touch)</option>
                    <option value="designer">Дизайнерский лен</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Срок выполнения
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setUrgency("standard")}
                    className={`py-2 px-3 rounded-lg border text-xs font-medium transition-colors ${
                      urgency === "standard"
                        ? "border-[#0a3d90] bg-blue-50/50 text-[#0a3d90] dark:bg-blue-950/30 dark:border-blue-700 dark:text-blue-300"
                        : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400"
                    }`}
                  >
                    Стандарт (2-3 дня)
                  </button>
                  <button
                    type="button"
                    onClick={() => setUrgency("express")}
                    className={`py-2 px-3 rounded-lg border text-xs font-medium transition-colors ${
                      urgency === "express"
                        ? "border-[#0a3d90] bg-blue-50/50 text-[#0a3d90] dark:bg-blue-950/30 dark:border-blue-700 dark:text-blue-300"
                        : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400"
                    }`}
                  >
                    Срочно (24 часа)
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <input
                  type="text"
                  required
                  placeholder="Ваше имя"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="px-3.5 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-xs sm:text-sm focus:outline-none focus:border-[#0a3d90]"
                />
                <input
                  type="tel"
                  required
                  placeholder="+7 (___) ___-__-__"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="px-3.5 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-xs sm:text-sm focus:outline-none focus:border-[#0a3d90] font-mono"
                />
              </div>

              <div className="mt-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-mono text-slate-500 dark:text-slate-400">
                    Ориентировочная сумма
                  </div>
                  <div className="text-xl font-semibold text-slate-900 dark:text-white tabular-nums">
                    ~ {calculatePrice()} ₽
                  </div>
                </div>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-lg text-xs sm:text-sm font-medium bg-[#0a3d90] dark:bg-[#3b82f6] text-white hover:bg-[#083070] dark:hover:bg-[#2563eb] flex items-center gap-1.5 transition-colors"
                >
                  <span>Заказать</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
